/**
 * Utility functions for handling and displaying error messages.
 */

import { imageReferenceRejectionCopy } from '@/i18n/image-reference-rejection-copy';
import { ConvexError } from 'convex/values';

import {
  getDocumentLocale,
  localizeWorkspaceErrorMessage,
} from '@/lib/workspace-error-message';

/** Whether a route-level error represents a permission failure. */
export function isAccessDeniedError(error: unknown): boolean {
  if (error == null) return false;

  const errorText =
    error instanceof Error
      ? `${error.name} ${error.message} ${String(
          (error as { data?: unknown }).data ?? ''
        )}`
      : String(error);

  return /\bforbidden\b|\baccess denied\b|not (?:authorized|allowed) to (?:access|view)/i.test(
    errorText
  );
}

/**
 * Extract a user-safe error message from an unknown thrown value.
 *
 * Only messages from `ConvexError` are trusted as user-facing — those are
 * the errors our backend explicitly raised for the client. Everything else
 * (validator failures, runtime exceptions, stack traces, network
 * internals) falls back to a generic message so we never leak raw
 * technical details into the UI.
 *
 * Server contract: throw `new ConvexError(message)` (or
 * `new ConvexError({ message, ...data })`) when the error should be shown
 * to the user. `ConvexError` is a class — always use `new`. Throw regular
 * `Error` for internal failures that should stay hidden.
 *
 * Backend codes and messages that name the organization are shown as
 * localized "workspace" copy. `locale` defaults to the document language.
 */
export function getUserFacingErrorMessage(
  error: unknown,
  fallback: string = 'Something went wrong. Please try again.',
  locale?: string
): string {
  const message = readConvexErrorMessage(error);
  if (!message) return fallback;
  return localizeWorkspaceErrorMessage(message, locale ?? getDocumentLocale());
}

/** The raw message a `ConvexError` carries, before any localization. */
export function readConvexErrorMessage(error: unknown): string | undefined {
  if (!(error instanceof ConvexError)) return undefined;
  const data = (error as { data?: unknown }).data;
  if (typeof data === 'string' && data.trim().length > 0) {
    return data;
  }
  if (data && typeof data === 'object' && 'message' in data) {
    const msg = (data as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim().length > 0) {
      return msg;
    }
  }
  const msg = (error as Error).message;
  if (typeof msg === 'string' && msg.trim().length > 0) {
    return msg;
  }
  return undefined;
}

/**
 * Common user-friendly error messages for known error patterns.
 */
const ERROR_PATTERNS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern:
      /flagged by a content checker|content could not be processed|content policy violation|violates our content policy|rejected by the safety system/i,
    message:
      "This request includes explicit or inappropriate content and can't be generated. Please try different images or prompts.",
  },
  {
    pattern: /Provider returned error/i,
    message: 'The AI service encountered an issue. Please try again.',
  },
  {
    pattern: /image.*not.*valid|invalid.*image|unsupported.*image/i,
    message:
      'One of the uploaded images could not be processed. Please try with a different image format (JPEG, PNG, WebP, or GIF).',
  },
  {
    pattern: /rate limit|too many requests/i,
    message: 'Too many requests. Please wait a moment and try again.',
  },
  {
    pattern: /timeout|timed out/i,
    message: 'The request took too long. Please try again.',
  },
  {
    pattern: /network|connection|fetch failed/i,
    message:
      'A network error occurred. Please check your connection and try again.',
  },
  {
    pattern: /insufficient.*credit|billing|payment/i,
    message:
      'Unable to process request due to account limits. Please contact support.',
  },
  {
    pattern: /authentication|unauthorized|forbidden/i,
    message: 'Authentication error. Please refresh the page and try again.',
  },
];

/**
 * Default user-friendly error message when no pattern matches.
 */
const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * Sanitizes an error message for display to users.
 *
 * In production, returns a user-friendly message instead of technical details.
 * In development, returns the full error message for debugging.
 *
 * @param errorMessage - The raw error message
 * @param options - Options for sanitization
 * @returns A sanitized error message suitable for display
 */
export function sanitizeErrorMessage(
  errorMessage: string | undefined | null,
  options?: {
    /**
     * Force showing user-friendly messages even in development.
     * Useful for testing the production behavior.
     */
    forceUserFriendly?: boolean;
  }
): string {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return DEFAULT_ERROR_MESSAGE;
  }

  const referenceCopy = imageReferenceRejectionCopy(
    errorMessage,
    getDocumentLocale()
  );
  if (referenceCopy) return referenceCopy;

  const isDevelopment = process.env.NODE_ENV === 'development';
  const forceUserFriendly = options?.forceUserFriendly ?? false;

  // In development (unless forced), show the full error for debugging
  if (isDevelopment && !forceUserFriendly) {
    // Still truncate extremely long messages
    const maxLength = 500;
    if (errorMessage.length > maxLength) {
      return errorMessage.substring(0, maxLength) + '...';
    }
    return errorMessage;
  }

  // In production, show user-friendly messages
  // Check for known error patterns
  for (const { pattern, message } of ERROR_PATTERNS) {
    if (pattern.test(errorMessage)) {
      return message;
    }
  }

  // If the error message is short and doesn't look like a stack trace,
  // it might already be user-friendly
  const isLikelyStackTrace =
    errorMessage.includes('at ') ||
    errorMessage.includes('Error:') ||
    errorMessage.includes('\n') ||
    errorMessage.length > 200;

  if (!isLikelyStackTrace && errorMessage.length < 100) {
    return errorMessage;
  }

  return DEFAULT_ERROR_MESSAGE;
}

/**
 * Checks if an error message looks like it contains technical details
 * that shouldn't be shown to end users.
 */
export function isTechnicalError(
  errorMessage: string | undefined | null
): boolean {
  if (!errorMessage) return false;

  return (
    errorMessage.includes('at ') ||
    errorMessage.includes('Error:') ||
    errorMessage.includes('node_modules') ||
    errorMessage.includes('convex:') ||
    errorMessage.includes('.ts:') ||
    errorMessage.includes('.js:') ||
    /\d+:\d+/.test(errorMessage) // Line:column patterns
  );
}

/**
 * A query or mutation that ran without a signed-in user: the Convex socket
 * lost its token for a moment (a refresh after the laptop sleeps) or the
 * session ended. Recoverable by re-authenticating, never a page error.
 */
export function isAuthLapseError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const data =
    error instanceof ConvexError && typeof error.data === 'string'
      ? error.data
      : '';
  return /^(?:UNAUTHORIZED|AUTH_REQUIRED|UNAUTHENTICATED)$/.test(data)
    ? true
    : /ConvexError: (?:UNAUTHORIZED|AUTH_REQUIRED|UNAUTHENTICATED)\b/.test(
        error.message
      );
}
